// The job page's view (BL-128, the job page study): the server's one read of a job (jobs.page) turned
// into the words, flags and links the page draws (@flowology/ui-web/job's JobView). Pure: no calls, no
// clock but the one passed in. What the viewer can't read came back null and what they can't do came
// back false in `can`, so it's simply not in the view: cost and profit are in it only for the owner
// (costing), a fix's control only for whoever clears it, a composer mode only for who may write it.
// JobRoute.tsx runs what the page hands back. Every word about a job's or visit's state is job-words'.
import {
  dateText,
  dateTimeText,
  localDateOf,
  sentences,
  timeText,
  windowText,
  zonedParts,
} from '@flowology/kernel';
import type {
  ComposeMode,
  HistoryFilter,
  HistoryKind,
  MenuItem,
  MenuSection,
  Photo,
  PhotoStage,
} from '@flowology/ui-web/fl';
import type {
  ActionLink,
  CompareView,
  JobContact,
  JobDetailItem,
  JobEntry,
  JobEstimate,
  JobFix,
  JobFormView,
  JobInvoiceView,
  JobPart,
  JobPhotosView,
  JobReviewView,
  JobView,
  JobVisit,
  StoryStop,
  ViewCell,
} from '@flowology/ui-web/job';
import {
  JOB_STATE_MARK,
  JOB_STATE_WORDS,
  TO_SCHEDULE,
  jobStateOf,
  visitWord,
  type JobState,
} from '@flowology/ui-web/words';
import {
  flagText,
  formAnswers,
  invoiceState,
  isPhoto,
  money,
  type Attachment,
  type Checklist,
  type ChecklistItem,
  type JobDetail,
  type JobForms,
  type ReviewMaterial,
  type TimelineEvent,
} from '../review/review-model.ts';
import {
  bookable,
  cancellable,
  estimateState,
  filledInText,
  jobFactsText,
  sendable,
  timeLoggedText,
  visitCancellable,
  type Estimate,
} from './job-model.ts';
import { partsAllIn, partsLineStands, partsShown, type PartsOrder } from './parts-model.ts';
import type { JobPurchase } from '../books/receipts-model.ts';
import type { Deposit } from './deposits-model.ts';
import type { JobMessage } from './thread-model.ts';
import type { Task } from '../tasks/tasks-model.ts';
import type { Message } from '../messages/messages-model.ts';
import { phoneText, type Customer } from '../types.ts';
import { clickLine, type AdClick } from '../shared/ad-click-model.ts';
import { campaignWord } from '../calls/source-model.ts';
import { gapsText } from '../settings/licences-model.ts';
import { pauseWords, type PauseReason } from '../board-model.ts';
import { confirmationWords } from './confirmation-model.ts';
import { scopeLead } from '@flowology/domain';
import { plainText } from '../shared/plain-text.ts';
import { qtyText } from '../inventory/model.ts';
import { rateText } from '../invoice/invoice-model.ts';
import { pageLabel } from '../blueprint/places.ts';
import { tileOf } from '../shared/photo-display-model.ts';

// ── what jobs.page sends ──

/** jobs.page's `can`: what the caller may do on the job, each from the permission of the action that does it. */
export const CAN_KEYS = [
  'complete',
  'sendBack',
  'cancel',
  'reopen',
  'addNote',
  'addVisit',
  'cancelVisit',
  'dispatch',
  'editInvoice',
  'voidInvoice',
  'sendInvoice',
  'setPo',
  'waiveSignature',
  'reviewMaterials',
  'setSplits',
  'resolveFlags',
  'buildEstimates',
  'sendEstimates',
  'approvePrice',
  'bookSold',
  'takeDeposit',
  'approvePurchases',
  'messageTechs',
  'textCustomer',
  'manageTasks',
  'editPhotos',
  'editDetails',
  'seeCost',
] as const;
export type CanKey = (typeof CAN_KEYS)[number];
export type JobCan = Partial<Record<CanKey, boolean>>;

type Stamp = { at: string; by: string | null };
type JobRef = { id: string; number: string; summary: string };

/** What the server holds on the job that jobs.get leaves out (jobs.page `more`). */
export interface JobMore {
  jobType: { id: string; name: string } | null;
  tags: { id: string; name: string }[];
  isWarranty: boolean;
  parentJob: JobRef | null;
  callbacks: JobRef[];
  booked: Stamp;
  completed: Stamp | null;
  cancelled: (Stamp & { reason: string | null }) | null;
  sentBack: { at: string; reason: string | null } | null;
  soldFromEstimate: { id: string; number: string; jobId: string; jobNumber: string } | null;
  sourceLead: { id: string; name: string | null; summary: string | null } | null;
  sourceCall: { id: string; at: string; from: string | null } | null;
  invoiceMode: string | null;
  address: {
    line1: string;
    line2: string | null;
    city: string;
    region: string | null;
    postalCode: string | null;
  };
  access: { notes: string | null; codes?: string | null } | null;
  visits: {
    appointmentId: string;
    arrivalWindow: { start: string; end: string } | null;
    outsideWindow: boolean;
    afterHours: { reason: string | null } | null;
    pauseReason: string | null;
    techs: {
      staffId: string;
      name: string;
      status: string;
      dispatchedAt: string | null;
      arrivedAt: string | null;
      pausedAt: string | null;
    }[];
  }[];
  invoices: {
    id: string;
    number: string;
    status: string;
    totalCents: number;
    balanceCents: number;
    isPrimary: boolean;
  }[];
  rating: { stars: number; comment: string | null; at: string } | null;
  membershipVisit: { membershipId: string; plan: string } | null;
  equipmentInstalled: { id: string; label: string }[];
  /** The summary read into its parts (the job summary, step 1); absent from a server before it. */
  summary?: JobSummaryParts;
  /** What an imported job's record adds with no column yet; null on Flowology's own jobs. */
  imported?: { customerPo: string | null; priorityLevel: string | null; buzzer?: string | null } | null;
}

/** jobs.page's `more.summary`: the summary's own words, a sold job's form, the dispatch lines, the original. */
export interface JobSummaryParts {
  scope: string;
  soldBy: string | null;
  workOrderOnFile: string | null;
  scheduling: string | null;
  other: string | null;
  dispatchNotes: string[];
  original: string;
  /** The original says more than the scope. */
  changed: boolean;
}

/** A file on the job as attachments.list sends it: what it's on (the job, or one of its visits). */
export type PageAttachment = Attachment & { entityType?: string; entityId?: string };

/** One job's costing row (reports.jobCosting): the owner's alone. */
export interface JobCostingRow {
  jobId: string;
  revenueCents: number;
  costOfSalesCents: number;
  grossProfitCents: number;
  marginPct: number | null;
  pendingPurchasesCents: number;
  labourCents: number;
}

/** jobs.page's answer. Each section is null when the caller couldn't read it on its own. */
export interface JobPageData {
  job: JobDetail;
  more: JobMore;
  estimates: { estimates: Estimate[] } | null;
  soldNotBooked: string[] | null;
  partsOnOrder: { orders: PartsOrder[] } | null;
  partsBought: { purchases: JobPurchase[] } | null;
  deposits: { deposits: Deposit[] } | null;
  photos: { attachments: PageAttachment[] } | null;
  forms: JobForms | null;
  review: Checklist | null;
  timeline: { events: TimelineEvent[] } | null;
  messages: { messages: Message[] } | null;
  techThread: { messages: JobMessage[] } | null;
  tasks: { tasks: Task[] } | null;
  customer: Customer | null;
  adClick: { click: AdClick | null } | null;
  costing: { rows: JobCostingRow[] } | null;
  can: JobCan;
}

export interface ViewOptions {
  timeZone: string;
  now: Date;
  /** The place the page was opened from: its name in the crumb. */
  crumb: string;
  /** The board's visit the page was opened from, opened and marked. */
  fromVisit?: string | null;
  /** The company mirrors another system: what that system owns can't be changed here. */
  mirroring?: boolean;
  /** Texting isn't set up for the company yet (messages.channels): nothing is texted (QA-324). */
  textingOff?: boolean;
  notice?: JobView['notice'];
}

// ── small words ──

const first = (name: string) => name.trim().split(/\s+/)[0] ?? name;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const join = (...parts: (string | null | undefined | false)[]) => parts.filter(Boolean).join(' · ');
const pad = (n: number) => String(n).padStart(2, '0');
/** An instant as the company's wall clock, "2026-10-25T08:20", as HistoryFeed groups and sorts it. */
export function localAt(at: string, timeZone: string): string {
  const p = zonedParts(new Date(at), timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}
const minutesText = (m: number) => (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`);
const minutesBetween = (a: string, b: string) =>
  Math.max(0, Math.round((Date.parse(b) - Date.parse(a)) / 60_000));

/** The address on one line, as a letter has it: "1184 Larch Way, Unit 2, Langford BC V9B 3K2". */
export function fullAddress(a: JobMore['address']): string {
  const place = [a.city, a.region, a.postalCode].filter(Boolean).join(' ');
  return [a.line1, a.line2, place].filter(Boolean).join(', ');
}

/** A link to directions to the address, opened in a new tab by the reader (never fetched). */
export const directionsHref = (address: string) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;

const DRAWN: Record<string, JobVisit['drawn']> = {
  scheduled: 'booked',
  dispatched: 'working',
  working: 'working',
  paused: 'paused',
  closed: 'done',
  cancelled: 'cancelled',
};

/** The history's filters: every kind, then the moments that tell the story, then each kind. */
export const HISTORY_FILTERS: HistoryFilter[] = [
  { id: 'all', label: 'All' },
  { id: 'key', label: 'Key moments', keyOnly: true },
  { id: 'talk', label: 'Calls, texts', kinds: ['call', 'text'] },
  { id: 'note', label: 'Notes', kinds: ['note'] },
  { id: 'visit', label: 'Visits', kinds: ['visit', 'book'] },
  { id: 'photo', label: 'Photos', kinds: ['photo', 'form'] },
  { id: 'money', label: 'Money', kinds: ['estimate', 'money', 'parts'] },
  { id: 'edit', label: 'Edits', kinds: ['edit'] },
];

// ── the parts of the view ──

/** The job's techs in the order they first went on it: each one's pen on the board (1–8). */
function pensOf(more: JobMore): Map<string, number> {
  const pens = new Map<string, number>();
  for (const v of more.visits)
    for (const t of v.techs) if (!pens.has(t.staffId)) pens.set(t.staffId, (pens.size % 8) + 1);
  return pens;
}

/** The customer's phone that may be texted about the job: consent, and nobody said not to. */
export function textablePhone(c: Customer | null) {
  if (!c || c.doNotContact) return null;
  const methods = c.contacts.flatMap((k) => k.methods.map((m) => ({ ...m, contact: k.name })));
  const ok = methods.filter((m) => m.kind === 'phone' && m.consent && !m.doNotContact);
  return ok.find((m) => m.isPrimary) ?? ok[0] ?? null;
}

/** What a visit's line says beyond its state: outside its window, after hours. */
function alsoOf(m: { outsideWindow?: boolean; afterHours?: { reason?: string | null } | null } | undefined) {
  return (
    join(
      m?.outsideWindow ? 'starts outside its window' : null,
      m?.afterHours ? `after hours${m.afterHours.reason ? `: ${m.afterHours.reason}` : ''}` : null,
    ) || null
  );
}

function visitRows(d: JobPageData, o: ViewOptions, can: JobCan): JobVisit[] {
  const { job, more } = d;
  const tz = o.timeZone;
  const pens = pensOf(more);
  const byId = new Map(more.visits.map((v) => [v.appointmentId, v]));
  return [...job.appointments]
    .sort((a, b) => a.start.localeCompare(b.start))
    .map((a, i) => {
      const m = byId.get(a.id);
      const techs = m?.techs ?? (a.techs ?? []).map((t) => ({ staffId: t.id, name: t.name }));
      const win = m?.arrivalWindow ?? { start: a.start, end: a.end };
      const live = a.status !== 'cancelled' && a.status !== 'closed';
      const gaps = live ? gapsText(a.licenceGaps ?? []) : null;
      const said = a.status === 'cancelled' && a.cancelReason ? `cancelled: ${a.cancelReason}` : null;
      const timed = m?.techs.filter((t) => t.dispatchedAt || t.arrivedAt) ?? [];
      const actions: ActionLink[] = [];
      if (can.cancelVisit && !o.mirroring && visitCancellable(job, a.id))
        actions.push({ id: 'cancel-visit', label: 'Cancel this visit', target: a.id, isDanger: true });
      actions.push({ id: 'board', label: 'See it on the board', target: a.id });
      return {
        id: a.id,
        n: i + 1,
        day: dateText(a.start, tz, { weekday: 'short', year: false, now: o.now }),
        window: windowText(win.start, win.end, tz),
        techs: techs.length ? techs.map((t) => t.name).join(', ') : 'No tech yet',
        pens: techs.map((t) => pens.get(t.staffId) ?? 1),
        drawn: DRAWN[a.status] ?? 'booked',
        word: visitWord(a.status).toLowerCase(),
        // why it's paused in the tech app's words ("Paused: work done"), never its key
        what: join(
          said ??
            (a.status === 'paused' && m?.pauseReason
              ? `${visitWord(a.status)}: ${pauseWords(m.pauseReason as PauseReason).toLowerCase()}`
              : visitWord(a.status)),
          alsoOf(m),
        ),
        ...(a.status === 'paused' && m?.pauseReason ? { pauseReason: m.pauseReason } : {}),
        ...(a.status === 'cancelled' && a.cancelReason ? { cancelReason: a.cancelReason } : {}),
        ...(alsoOf(m) ? { also: alsoOf(m)! } : {}),
        ...(gaps ? { how: gaps } : {}),
        ...(live && confirmationWords(a.confirmation, tz)
          ? { instructions: confirmationWords(a.confirmation, tz)! }
          : {}),
        ...(timed.length
          ? {
              times: timed.map((t) => ({
                id: `${a.id}-${t.staffId}`,
                tech: t.name,
                sent: '—',
                onTheWay: t.dispatchedAt ? timeText(t.dispatchedAt, tz) : '—',
                arrived: t.arrivedAt ? timeText(t.arrivedAt, tz) : '—',
                stopped: t.pausedAt ? timeText(t.pausedAt, tz) : '—',
                onSite: t.arrivedAt
                  ? t.pausedAt
                    ? minutesText(minutesBetween(t.arrivedAt, t.pausedAt))
                    : 'still there'
                  : '—',
              })),
            }
          : {}),
        actions,
        ...(o.fromVisit === a.id ? { cameFrom: true } : {}),
      };
    });
}

/** The estimate the customer signed, and the option they chose. */
function signedOf(d: JobPageData) {
  for (const e of d.estimates?.estimates ?? []) {
    if (e.status !== 'sold' || !e.selectedOptionId) continue;
    const o = e.options.find((x) => x.id === e.selectedOptionId);
    if (o) return { estimate: e, option: o, name: optionName(e, o.id) };
  }
  return null;
}
const optionName = (e: Estimate, id: string) => {
  const i = e.options.findIndex((o) => o.id === id);
  return e.options.length > 1 ? `Option ${String.fromCharCode(65 + Math.max(0, i))}` : 'The estimate';
};

const lc = (s: string) => s.trim().toLowerCase();

/** Sold, billed and used, line by line: the owner's view at review (a cost isn't in it, only gaps). */
function compareOf(d: JobPageData): CompareView | undefined {
  const s = signedOf(d);
  const inv = d.job.invoice;
  if (!s || !inv) return undefined;
  const mats = d.review?.materials ?? [];
  const names = techNames(d);
  const rows = new Map<string, { item: string; sold: number | null; billed: number | null; used: string }>();
  for (const l of s.option.lines)
    rows.set(lc(l.description), { item: l.description, sold: l.totalCents, billed: null, used: '' });
  for (const l of inv.lines) {
    const r = rows.get(lc(l.description));
    if (r) r.billed = (r.billed ?? 0) + l.totalCents;
    else rows.set(lc(l.description), { item: l.description, sold: null, billed: l.totalCents, used: '' });
  }
  for (const m of mats) {
    const r = rows.get(lc(m.name));
    const who = m.staffId ? `${names.get(m.staffId) ?? 'a tech'}’s truck` : 'used on the job';
    if (r) r.used = who;
    else rows.set(lc(m.name), { item: m.name, sold: null, billed: null, used: who });
  }
  const billed = inv.subtotalCents;
  const sold = s.option.subtotalCents;
  const short = sold - billed;
  const cell = (r: { sold: number | null; billed: number | null }): ViewCell =>
    r.billed !== null
      ? money(r.billed)
      : r.sold !== null
        ? { value: 'missing', tone: 'bad' }
        : { value: 'not billed', tone: 'none' };
  return {
    soldSub: s.name.toLowerCase(),
    billedSub: inv.number,
    rows: [...rows.entries()].map(([id, r]) => ({
      id,
      item: r.item,
      sold: r.sold !== null ? money(r.sold) : { value: 'not sold', tone: 'none' },
      billed: cell(r),
      used: r.used || '—',
    })),
    foot: {
      sold: money(sold),
      billed: money(billed),
      used:
        short > 0
          ? { value: `${money(short)} short`, tone: 'bad' }
          : short < 0
            ? `${money(-short)} over`
            : 'matches',
    },
  };
}

function techNames(d: JobPageData): Map<string, string> {
  const names = new Map<string, string>();
  for (const v of d.more.visits) for (const t of v.techs) names.set(t.staffId, t.name);
  for (const a of d.job.appointments) for (const t of a.techs ?? []) names.set(t.id, t.name);
  return names;
}

/** The lead on the latest visit: who a job sent back goes to. */
function leadTech(d: JobPageData): string | null {
  const last = [...d.more.visits].reverse().find((v) => v.techs.length);
  return last?.techs[0]?.name ?? null;
}

/** What a checklist line asks, where it's fixed off this page. */
const ELSEWHERE: Partial<Record<ChecklistItem['key'], string>> = {
  visits: 'The tech closes the visit on the iPad',
  forms: 'The tech fills it in on the iPad',
  photos: 'The tech adds them on the iPad',
  overpaid: 'Refund it, or keep it as credit from the invoice',
};

function reviewOf(d: JobPageData, o: ViewOptions, can: JobCan, s: JobState): JobReviewView | undefined {
  const c = d.review;
  if (!c) return undefined;
  const tz = o.timeZone;
  const names = techNames(d);
  const mirroring = !!o.mirroring;
  const reviewer = !!can.complete;
  const theirs = { kind: 'note' as const, text: 'For whoever reviews the job' };
  const fixes: JobFix[] = [];
  for (const i of c.items) {
    if (i.key === 'materials' || i.key === 'flags') continue; // each material and flag is its own, below
    if (i.ok) continue;
    const fix: JobFix = {
      id: i.key,
      title: i.message,
      detail: i.blocking ? 'Holds up completing' : 'A heads-up',
      isCleared: false,
    };
    if (i.key === 'signature' && can.waiveSignature && d.job.invoice && !mirroring)
      fix.fix = { kind: 'links', links: [{ id: 'why', label: 'Say why it’s missing' }] };
    else if (i.key === 'po' && can.setPo && d.job.invoice && !mirroring)
      fix.fix = { kind: 'links', links: [{ id: 'po', label: 'Add the PO' }] };
    else if (ELSEWHERE[i.key]) fix.fix = { kind: 'note', text: ELSEWHERE[i.key]! };
    else if (!reviewer) fix.fix = theirs;
    fixes.push(fix);
  }
  for (const m of c.materials) {
    const who = m.staffId ? (names.get(m.staffId) ?? 'The tech') : 'The tech';
    fixes.push({
      id: `mat:${m.id}`,
      title: `Bill ${m.name}?`,
      detail: join(
        `${qtyText(m.quantity)} × ${money(m.unitPriceCents)} = ${money(m.totalCents)}`,
        `${who} ${m.billProposed ? 'proposed billing it' : 'proposed not billing it'}`,
        alreadyBilled(m),
      ),
      isCleared: m.decision !== null,
      fix:
        can.reviewMaterials && !mirroring
          ? {
              kind: 'choice',
              label: `Bill ${m.name}?`,
              options: [
                { id: 'bill', label: 'Bill it' },
                { id: 'no_bill', label: 'Keep in the price' },
              ],
              selected: m.decision,
            }
          : theirs,
    });
  }
  for (const f of c.flags) {
    const photo = f.reason === 'tech_asked_to_hide' && f.entityType === 'attachment';
    const links: ActionLink[] = photo
      ? [
          { id: 'see', label: 'See the photo', target: `photo:${f.entityId}` },
          ...(can.editPhotos
            ? [
                { id: 'hide', label: 'Hide it from the customer' },
                { id: 'show', label: 'Keep showing it' },
              ]
            : []),
        ]
      : can.resolveFlags
        ? [
            { id: 'resolved', label: 'Resolve' },
            { id: 'dismissed', label: 'Dismiss' },
          ]
        : [];
    fixes.push({
      id: `flag:${f.id}`,
      title: flagText(f.reason),
      detail: `Flagged ${dateTimeText(f.createdAt, tz, { year: false, now: o.now })}`,
      isCleared: false,
      fix: links.length ? { kind: 'links', links } : theirs,
    });
  }
  const left = fixes.filter(
    (f) =>
      !f.isCleared &&
      (f.id.startsWith('mat:') ||
        f.id.startsWith('flag:') ||
        c.items.some((i) => i.key === f.id && i.blocking)),
  ).length;
  const open = fixes.filter((f) => !f.isCleared).map((f) => f.title.replace(/\?$/, ''));
  const clear = c.items
    .filter((i) => i.ok && i.key !== 'materials' && i.key !== 'flags')
    .map((i) => i.message);
  const waiting = s === 'review';
  const lead = leadTech(d);
  const more: ActionLink[] = [];
  if (can.setSplits && !mirroring && c.splits.length > 1)
    more.push({ id: 'splits', label: 'Who shares the credit' });
  if (can.addVisit && !mirroring) more.push({ id: 'addvisit', label: 'Add a visit' });
  const compare = can.seeCost && d.costing ? compareOf(d) : undefined;
  return {
    summary: left
      ? { ...(reviewer ? {} : { lead: 'For the office:' }), text: sentences(...open), isBad: true }
      : {
          text: waiting
            ? reviewer
              ? 'All clear, ready to complete'
              : 'Ready for the office to complete'
            : 'Nothing to clear so far',
          isBad: false,
        },
    ...(d.more.sentBack
      ? {
          handback: {
            by: `Sent back ${dateTimeText(d.more.sentBack.at, tz, { year: false, now: o.now })}`,
            text: d.more.sentBack.reason ?? 'No reason given.',
          },
        }
      : {}),
    fixes,
    left,
    ...(reviewer && waiting && !left
      ? { allClear: 'Nothing left to clear. Complete the job from the top of the page.' }
      : {}),
    clearLine: clear.length
      ? `Clear: ${clear.join(' · ')}`
      : reviewer
        ? 'Nothing is clear yet.'
        : 'Only whoever reviews the job can clear these and complete it.',
    ...(can.sendBack && !mirroring && d.job.status === 'awaiting_review' && lead
      ? { sendBackTo: first(lead) }
      : {}),
    ...(more.length ? { more } : {}),
    ...(compare ? { compare } : {}),
  };
}

function estimatesOf(d: JobPageData, o: ViewOptions, can: JobCan): JobView['estimates'] {
  if (!d.estimates) return undefined;
  const tz = o.timeZone;
  const list = d.estimates.estimates;
  const live = cancellable(d.job.status);
  const unbooked = new Set(d.soldNotBooked ?? []);
  const mirroring = !!o.mirroring;
  const est: JobEstimate[] = list.map((e) => {
    const signed = e.status === 'sold' ? e.selectedOptionId : null;
    const actions: ActionLink[] = [{ id: 'estimate-open', label: 'Open it', target: e.id }];
    if (can.buildEstimates && !mirroring && live && !e.frozen && e.status !== 'sold')
      actions.push({ id: 'estimate-edit', label: 'Edit', target: e.id });
    if (can.sendEstimates && !mirroring && sendable(e))
      actions.push({ id: 'estimate-send', label: 'Send', target: e.id });
    if (can.bookSold && !mirroring && bookable(e) && unbooked.has(e.id))
      actions.push({ id: 'book', label: 'Book the work', target: e.id });
    if (e.status === 'sold' && d.deposits) actions.push({ id: 'deposits', label: 'Deposits', target: e.id });
    const waits = e.options.some((x) => x.margin?.waits && !x.margin.approved);
    return {
      id: e.id,
      ...(list.length > 1
        ? { title: `Estimate ${e.number} · ${plainText(e.title) || estimateState(e)}` }
        : {}),
      options: e.options.map((x) => ({
        id: x.id,
        name: optionName(e, x.id),
        title: plainText(x.title) || x.tier || 'Option',
        price: money(x.totalCents),
        points: x.summary ? [plainText(x.summary)] : x.lines.slice(0, 3).map((l) => plainText(l.description)),
        ...(signed === x.id ? { isSigned: true } : {}),
      })),
      foot: join(
        estimateState(e),
        e.signature
          ? `signed by ${e.signature.signerName}, ${dateTimeText(e.signature.signedAt, tz, { year: false, now: o.now })}`
          : null,
        e.soldBy ? `sold by ${e.soldBy}` : null,
        waits ? 'a price waits for the office’s OK' : null,
      ),
      actions,
    };
  });
  const s = signedOf(d);
  return {
    summary: s
      ? join(
          `${s.name}, ${money(s.option.totalCents)}`,
          s.estimate.signature
            ? `signed ${dateText(s.estimate.signature.signedAt, tz, { weekday: 'short', year: false, now: o.now })}`
            : 'sold',
          list.length > 1
            ? plural(list.length, 'estimate')
            : `${plural(s.estimate.options.length, 'option')} shown`,
        )
      : list.length
        ? join(plural(list.length, 'estimate'), list[0] ? estimateState(list[0]) : null)
        : 'None yet',
    list: est,
    actions: can.buildEstimates && !mirroring && live ? [{ id: 'estimate-new', label: 'New estimate' }] : [],
  };
}

const PURCHASE_WORDS: Record<JobPurchase['status'], string> = {
  submitted: 'Waiting for approval',
  approved: 'Approved',
  void: 'Void',
  imported: 'Imported',
};

/** A material the invoice carries already, so billing it adds nothing or only the rest (QA-797). */
function alreadyBilled(m: ReviewMaterial): string | null {
  const on = Number(m.onInvoiceQuantity ?? 0);
  if (on <= 0) return null;
  const rest = Number(m.quantity) - on;
  return rest > 0
    ? `${qtyText(String(on))} already on the invoice, so billing it adds ${qtyText(String(rest))}`
    : 'already on the invoice, so billing it adds nothing';
}

const MATERIAL_WORDS = { bill: 'Billed', no_bill: 'Kept in the price' } as const;

function partsOf(d: JobPageData, can: JobCan): JobView['parts'] {
  // the parts used off the truck (QA-797): the reviewer's list, with what the office decided
  if (!d.partsOnOrder && !d.partsBought) return undefined;
  const truck = (d.review?.materials ?? []).filter((m) => Number(m.quantity) > 0);
  const showCost = !!can.seeCost && !!d.costing;
  const orders = partsShown(d.partsOnOrder?.orders ?? []);
  const bought = (d.partsBought?.purchases ?? []).filter((p) => p.kind === 'purchase');
  const rows: JobPart[] = [];
  for (const po of orders)
    for (const [i, l] of po.lines.entries())
      rows.push({
        id: `${po.poNumber}-${i}`,
        part: plainText(l.description),
        from: `${po.vendorName} · ${po.poNumber}`,
        // what came in, what was cancelled and what's still to come (QA-743)
        stands: partsLineStands(l),
      });
  for (const p of bought)
    rows.push({
      id: p.id,
      part: p.lines.map((l) => plainText(l.description)).join(', ') || p.number,
      from: join(
        p.vendorName ?? p.supplierName ?? 'A store',
        p.staffName ? `bought by ${p.staffName}` : null,
      ),
      ...(showCost && p.totalCents !== undefined ? { cost: money(p.totalCents) } : {}),
      stands: PURCHASE_WORDS[p.status],
    });
  const techs = techNames(d);
  for (const m of truck)
    rows.push({
      id: `mat-${m.id}`,
      part: `${qtyText(m.quantity)} × ${m.name}`,
      from: m.staffId ? `${techs.get(m.staffId) ?? 'A tech'}’s truck` : 'Used on the job',
      stands: m.decision
        ? MATERIAL_WORDS[m.decision]
        : Number(m.onInvoiceQuantity ?? 0) >= Number(m.quantity)
          ? 'On the invoice already'
          : 'To decide at review',
    });
  const allIn = partsAllIn(orders);
  const pending = bought.filter((p) => p.status === 'submitted').length;
  const cost = showCost
    ? bought.filter((p) => p.status !== 'void').reduce((a, p) => a + (p.totalCents ?? 0), 0)
    : 0;
  return {
    summary: rows.length
      ? join(
          orders.length
            ? allIn
              ? 'Everything ordered is in'
              : plural(orders.length, 'order') + ' on the way'
            : null,
          bought.length ? plural(bought.length, 'receipt') : null,
          truck.length ? `${plural(truck.length, 'part')} used off the truck` : null,
          pending ? `${pending} waiting for approval` : null,
          showCost && cost ? `cost ${money(cost)}` : null,
        )
      : 'Nothing ordered or bought for it',
    showCost,
    rows,
    ...(allIn && cancellable(d.job.status)
      ? { note: 'Everything ordered is in: book the return visit.' }
      : {}),
    actions: d.partsBought
      ? [{ id: 'receipts', label: can.approvePurchases ? 'Receipts and approvals' : 'Receipts' }]
      : [],
  };
}

function invoiceOf(d: JobPageData, o: ViewOptions, can: JobCan): JobInvoiceView | undefined {
  const inv = d.job.invoice;
  if (!inv) return undefined;
  const tz = o.timeZone;
  const mirroring = !!o.mirroring;
  const actions: ActionLink[] = [];
  if (inv.status === 'draft' && can.editInvoice && !mirroring)
    actions.push({ id: 'lines', label: 'Change the lines' });
  if (inv.status !== 'void')
    actions.push({ id: 'invoice-send', label: inv.status === 'draft' ? 'PDF' : 'Send, link or PDF' });
  actions.push({ id: 'invoice', label: 'Open the invoice', target: inv.id });
  const held = (d.deposits?.deposits ?? []).filter(
    (x) => x.reservedInvoiceId === inv.id || (x.state === 'applied' && x.appliedCents > 0),
  );
  const signed = signedOf(d);
  const row = d.costing?.rows.find((r) => r.jobId === d.job.id);
  const others = d.more.invoices.filter((x) => x.id !== inv.id);
  return {
    number: inv.number,
    summary: join(
      inv.number,
      invoiceState(inv),
      `${money(inv.totalCents)}${inv.taxCents ? ' with tax' : ''}`,
      inv.balanceCents > 0 && inv.status !== 'draft' ? `${money(inv.balanceCents)} owing` : null,
    ),
    actions,
    lines: inv.lines.map((l) => ({
      id: l.id,
      label: plainText(l.description),
      qty: qtyText(l.quantity),
      price: money(l.unitPriceCents),
      amount: money(l.totalCents),
    })),
    emptyNote: 'No lines yet. The tech adds them on the iPad as the work is done.',
    totals: [
      ...(inv.lines.length
        ? [
            { label: 'Subtotal', value: money(inv.subtotalCents) },
            ...inv.taxes.map((t) => ({ label: `${t.code} ${rateText(t.rate)}`, value: money(t.taxCents) })),
            { label: 'Total', value: money(inv.totalCents), kind: 'strong' as const },
          ]
        : []),
      ...held.map((x) => ({
        label: join(
          `Deposit${x.receivedAt ? `, ${dateText(x.receivedAt, tz, { weekday: 'short', year: false, now: o.now })}` : ''}`,
          x.method,
        ),
        value: money(-(x.appliedCents || x.amountCents)),
        kind: 'muted' as const,
      })),
      ...(inv.lines.length || inv.balanceCents
        ? [{ label: 'Owing', value: money(inv.balanceCents), kind: 'due' as const }]
        : []),
    ],
    ...(signed && inv.lines.length
      ? {
          matchNote: {
            text:
              inv.subtotalCents === signed.option.subtotalCents
                ? `Matches ${lower(signed.name)} as signed, ${money(signed.option.subtotalCents)} before tax`
                : inv.subtotalCents < signed.option.subtotalCents
                  ? `${money(signed.option.subtotalCents - inv.subtotalCents)} less than ${lower(signed.name)} as signed`
                  : `${money(inv.subtotalCents - signed.option.subtotalCents)} more than ${lower(signed.name)} as signed`,
            link: { id: 'estimate', label: 'See the estimate' },
          },
        }
      : {}),
    ...(can.seeCost && row
      ? {
          margin: {
            cost: money(row.costOfSalesCents),
            parts: join(
              `labour ${money(row.labourCents)}`,
              row.pendingPurchasesCents
                ? `${money(row.pendingPurchasesCents)} in receipts not approved yet`
                : null,
            ),
            profit: `${money(row.grossProfitCents)}${row.marginPct !== null ? `, ${Math.round(row.marginPct)}%` : ''}`,
          },
        }
      : {}),
    ...(others.length
      ? {
          others: `Also on the job: ${others.map((x) => join(x.number, x.status.replace(/_/g, ' '), money(x.totalCents))).join('; ')}`,
        }
      : {}),
  };
}

/**
 * A photo's files: one the tech marked up (BL-025) is sent as its markup (url) and the photo as taken
 * (originalUrl), and the viewer switches between them; any other is its display copy, or the original.
 */
export function pictureOf(a: Pick<Attachment, 'url' | 'originalUrl' | 'annotated'>): {
  src: string;
  markupSrc?: string;
} {
  if (a.annotated && a.url && a.originalUrl) return { src: a.originalUrl, markupSrc: a.url };
  return { src: a.url ?? '' };
}

/** A photo with no file to show yet: what it says while it waits ("Uploading…", "Checking…"); else null. */
export function waitingOf(a: PageAttachment): string | null {
  if (a.url) return null;
  const t = tileOf(a);
  return t.t === 'waiting' && t.words !== 'No preview' ? t.words : null;
}

function photosOf(
  d: JobPageData,
  o: ViewOptions,
  can: JobCan,
  visits: JobVisit[],
): JobPhotosView | undefined {
  if (!d.photos && !d.forms && !d.job.signatures?.length) return undefined;
  const tz = o.timeZone;
  const files = d.photos?.attachments ?? [];
  const visitN = new Map(visits.map((v) => [v.id, v]));
  const whereOf = (a: PageAttachment) => {
    const v = a.entityType === 'appointment' && a.entityId ? visitN.get(a.entityId) : undefined;
    const stage = a.category && a.category !== 'other' ? a.category.replace(/_/g, ' ') : null;
    return join(v ? `Visit ${v.n}` : 'On the job', stage);
  };
  // a photo whose file hasn't landed yet shows as on its way, as the old sheet's strip did (QA-744)
  const shown = files.filter((a) => isPhoto(a) && a.status !== 'failed' && (a.url || waitingOf(a)));
  const photos: Photo[] = shown.map((a) => ({
    id: a.id,
    ...pictureOf(a),
    ...(waitingOf(a) ? { waiting: waitingOf(a)! } : {}),
    caption:
      plainText(a.caption) ||
      `${a.category === 'other' ? 'A' : a.category.replace(/_/g, ' ')} photo`.replace(/^./, (c) =>
        c.toUpperCase(),
      ),
    by: join(a.addedBy, a.capturedAt ? dateTimeText(a.capturedAt, tz, { year: false, now: o.now }) : null),
    where: whereOf(a),
    ...(a.visibility === 'internal' ? { officeOnly: true } : {}),
  }));
  const groupOf = (a: PageAttachment) =>
    a.entityType === 'appointment' && a.entityId && visitN.has(a.entityId) ? a.entityId : 'job';
  const groupIds = [...new Set(shown.map(groupOf))].sort(
    (a, b) => (visitN.get(a)?.n ?? 999) - (visitN.get(b)?.n ?? 999),
  );
  const groups = groupIds.map((g) => {
    const mine = shown.filter((a) => groupOf(a) === g);
    const stages = new Map<string, PhotoStage>();
    for (const a of mine) {
      const key = a.category || 'other';
      const st = stages.get(key) ?? {
        id: key,
        label: key === 'other' ? 'photos' : key.replace(/_/g, ' '),
        photos: [],
      };
      st.photos.push(photos.find((p) => p.id === a.id)!);
      stages.set(key, st);
    }
    const v = visitN.get(g);
    return {
      id: g,
      title: v ? `Visit ${v.n}` : 'On the job',
      ...(v ? { sub: v.day } : {}),
      stages: [...stages.values()],
    };
  });
  const forms: JobFormView[] = (d.forms?.submissions ?? []).map((sub) => {
    const f = formAnswers(sub);
    return {
      id: sub.id,
      title: f.name,
      meta: filledInText(sub, tz),
      items: f.rows.map((r) => ({
        label: r.label,
        value: r.answer,
        ...(r.answer === 'Not answered' ? { isBad: true } : {}),
      })),
      ...(!sub.complete ? { defaultOpen: true } : {}),
    };
  });
  const signatures = (d.job.signatures ?? []).map((g) => ({
    id: g.signatureId,
    title: 'Signed for the work',
    meta: join(
      g.signerName,
      dateTimeText(g.signedAt, tz, { year: false, now: o.now }),
      g.channel === 'device' ? 'on the iPad' : 'from the invoice’s link',
      g.invoiceNumber,
    ),
    ...(g.imageUrl ? { src: g.imageUrl } : {}),
    alt: g.imageUrl ? `${g.signerName}’s signature` : `${g.signerName} signed; the pad hasn’t arrived yet.`,
  }));
  if (!photos.length && !forms.length && !signatures.length && !files.length && !d.photos) return undefined;
  const office = photos.filter((p) => p.officeOnly).length;
  const missingForms = (d.forms?.required ?? []).filter((r) => !r.done).length;
  const uploading = photos.filter((p) => p.waiting).length;
  return {
    photos,
    groups,
    summary: join(
      photos.length ? plural(photos.length, 'photo') : 'No photos',
      uploading ? `${uploading} uploading` : null,
      forms.length ? plural(forms.length, 'form') : null,
      missingForms ? `${missingForms} to fill in` : null,
      signatures.length ? 'signed' : null,
    ),
    note: !photos.length
      ? 'No photos yet. The techs take them on the iPad.'
      : office
        ? `${office} of ${photos.length} say office: only the office sees them. The rest show on the invoice and in the customer’s portal.`
        : 'All of them show on the invoice and in the customer’s portal.',
    forms,
    signatures,
    actions: d.photos
      ? [
          {
            id: 'gallery',
            label: files.length > photos.length ? 'All photos and files' : 'Captions, videos and files',
          },
        ]
      : [],
    canShare: !!can.editPhotos,
  };
}

// ── the history: one feed ──

const TIMELINE_KIND: Record<TimelineEvent['kind'], HistoryKind> = {
  comms: 'call',
  office: 'edit',
  time: 'visit',
  photo: 'photo',
  note: 'note',
  estimate: 'estimate',
  material: 'parts',
  form: 'form',
  payment: 'money',
  pause: 'visit',
};

function kindOf(e: TimelineEvent): HistoryKind {
  if (e.kind === 'comms') return e.entityType === 'call' ? 'call' : 'text';
  if (e.kind === 'office') {
    if (/^Booking/.test(e.title)) return 'book';
    if (/^Dispatch/.test(e.title)) return 'visit';
    if (e.entityType === 'invoice') return 'money';
  }
  return TIMELINE_KIND[e.kind];
}
const keyMoment = (e: TimelineEvent) =>
  (e.kind === 'office' && /^Booking/.test(e.title)) ||
  // a completion the job's own stamp no longer holds (it was reopened since), and the reopen (QA-827)
  (e.kind === 'office' && /^(Complete a job|Reopen a completed job)$/.test(e.title)) ||
  (e.kind === 'estimate' && /sold|signed/i.test(e.title)) ||
  e.kind === 'payment' ||
  e.kind === 'pause' ||
  (e.kind === 'time' && e.title === 'Arrived');
const near = (a: string, b: string | undefined) => !!b && Math.abs(Date.parse(a) - Date.parse(b)) < 60_000;

/** "Sam Rivera" → "Sam". */
const firstName = (name: string) => name.split(/\s+/)[0] ?? name;
const listed = (names: string[]) =>
  names.length < 2 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
const lower = (t: string) => t.replace(/^./, (c) => c.toLowerCase());

/**
 * A timeline event as a sentence in the story's words ("Dana dispatched visit 1 to Sam", "Sam arrived",
 * "Sam paused: work done"), or null when the feed already tells it (the job's own booking, completing
 * or cancelling, a key moment of its own) or there is nothing to tell (a 0-minute stretch of work).
 */
/** An action's name in the past, person first: "Complete a job" → "completed the job". */
const PAST: Record<string, string> = {
  Add: 'added',
  Apply: 'applied',
  Approve: 'approved',
  Assign: 'assigned',
  Bill: 'billed',
  Book: 'booked',
  Cancel: 'cancelled',
  Change: 'changed',
  Clear: 'cleared',
  Close: 'closed',
  Complete: 'completed',
  Create: 'created',
  Decline: 'declined',
  Delete: 'deleted',
  Dismiss: 'dismissed',
  Edit: 'edited',
  Email: 'emailed',
  Finalize: 'finalized',
  Issue: 'issued',
  Keep: 'kept',
  Mark: 'marked',
  Move: 'moved',
  Open: 'opened',
  Pay: 'paid',
  Present: 'presented',
  Record: 'recorded',
  Refund: 'refunded',
  Remove: 'removed',
  Reopen: 'reopened',
  Request: 'requested',
  Reserve: 'reserved',
  Resolve: 'resolved',
  Review: 'reviewed',
  Send: 'sent',
  Set: 'set',
  Sign: 'signed',
  Split: 'split',
  Take: 'took',
  Text: 'texted',
  Update: 'updated',
  Void: 'voided',
  Waive: 'waived',
  Write: 'wrote',
};
export function pastAction(what: string): string | null {
  const m = /^([A-Z][a-z]+)\b(.*)$/.exec(what);
  const v = m ? PAST[m[1]!] : undefined;
  return v ? `${v}${m![2]!.replace(/^ an?( completed)? job\b/, ' the job')}` : null;
}
/** Estimate E-9143 written → "wrote estimate E-9143"; sent to the customer → "sent estimate E-9143 to the customer". */
const ESTIMATE_STEP: Record<string, (n: string) => string> = {
  written: (n) => `wrote estimate ${n}`,
  presented: (n) => `presented estimate ${n}`,
  'sent to the customer': (n) => `sent estimate ${n} to the customer`,
  sold: (n) => `sold estimate ${n}`,
  declined: (n) => `marked estimate ${n} declined`,
  expired: (n) => `marked estimate ${n} expired`,
};

export function timelineSentence(
  e: TimelineEvent,
  ctx: { visitNo: (appointmentId: string) => number | null; techsOf: (appointmentId: string) => string[] },
): string | null {
  const who = e.who ?? null;
  const group = /^(Booking|Dispatch): (.*)$/.exec(e.title);
  if (e.kind === 'office') {
    // the job's own booking and cancelling are their key moments' rows
    if (/^(Book|Cancel) a job$/.test(group?.[2] ?? e.title)) return null;
    const n = e.entityType === 'appointment' ? ctx.visitNo(e.entityId) : null;
    const visit = n ? `visit ${n}` : 'a visit';
    const techs = e.entityType === 'appointment' ? ctx.techsOf(e.entityId) : [];
    const what = group?.[2] ?? e.title;
    const by = who ?? 'The office';
    if (/^Dispatch/i.test(what))
      return `${by} dispatched ${visit}${techs.length ? ` to ${listed(techs)}` : ''}`;
    if (/^(Move|Reschedule)/i.test(what)) return `${by} moved ${visit}`;
    if (/^(Assign|Give)/i.test(what))
      return `${by} gave ${visit}${techs.length ? ` to ${listed(techs)}` : ''}`;
    if (/^(Take|Undispatch)/i.test(what)) return `${by} took ${visit} back`;
    if (/^Cancel a visit/i.test(what)) return `${by} cancelled ${visit}`;
    if (/^Add a (return )?visit/i.test(what)) return `${by} added ${visit}`;
    // any other action, in one voice with the rest: "Brook completed the job"
    const past = pastAction(what);
    return who ? (past ? `${who} ${past}` : `${who}: ${what}`) : what;
  }
  if (e.kind === 'time') {
    const t = /^(Drove|Worked|Off the job) · (.*)$/.exec(e.title);
    if (t) {
      if (/^0 min$/.test(t[2]!)) return null;
      const who2 = who ?? 'The tech';
      if (t[2] === 'still going')
        return `${who2} ${t[1] === 'Drove' ? 'is driving' : t[1] === 'Worked' ? 'is working' : 'is off the job'}, still going`;
      return `${who2} ${t[1] === 'Off the job' ? 'was off the job' : lower(t[1]!)} ${t[2]}`;
    }
    if (e.title === 'Arrived') {
      // "Dana for Sam": the office marked the tech arrived
      const f = who ? /^(.*) for (.*)$/.exec(who) : null;
      return f ? `${f[1]} marked ${f[2]} arrived` : `${who ?? 'The tech'} arrived`;
    }
  }
  if (e.kind === 'pause') {
    const why = /^Paused · (.*)$/.exec(e.title)?.[1] ?? null;
    const f = who ? /^(.*) for (.*)$/.exec(who) : null;
    const said = why && why !== 'no reason given' ? `: ${why}` : '';
    return f ? `${f[1]} paused ${f[2]}’s visit${said}` : `${who ?? 'The tech'} paused${said}`;
  }
  if (e.kind === 'form') {
    const f = /^(.*) form (filled in|started)$/.exec(e.title);
    if (f) return `${who ?? 'The tech'} ${f[2] === 'started' ? 'started' : 'filled in'} the ${f[1]} form`;
  }
  if (who) {
    // the rest person first, past tense: "Brook wrote estimate E-9143", "Dana recorded a payment on INV-18402"
    const est = /^Estimate (\S+) (written|presented|sent to the customer|sold|declined|expired)$/.exec(
      e.title,
    );
    if (est) return `${who} ${ESTIMATE_STEP[est[2]!]!(est[1]!)}`;
    const cleared = /^(Hold|Request to cancel) cleared: (.*)$/.exec(e.title);
    if (cleared) return `${who} cleared the ${lower(cleared[1]!)}${cleared[2] ? `: ${cleared[2]}` : ''}`;
    if (e.kind === 'note') return `${who} wrote a note`;
    if (e.kind === 'material') return `${who} used ${e.title}`;
    if (e.kind === 'photo') return `${who} added ${/^[aeiou]/i.test(e.title) ? 'an' : 'a'} ${lower(e.title)}`;
    const call = /^Call (in|out)(?: · (.*))?$/.exec(e.title);
    if (call)
      return `${who} ${call[1] === 'in' ? 'took a call' : 'called the customer'}${call[2] ? `, ${call[2]}` : ''}`;
    const sent = /^(Text|Email)(?: to the customer)? · (.*)$/.exec(e.title);
    if (sent)
      return `${who} ${sent[1] === 'Text' ? 'texted' : 'emailed'} the customer, ${sent[2]!.split(' · ').map(lower).join(', ')}`;
    const inv = /^Invoice (\S+) finalized$/.exec(e.title);
    if (inv) return `${who} finalized invoice ${inv[1]}`;
    const pay = /^Payment on (\S+)(?: · (.*))?$/.exec(e.title);
    if (pay) return `${who} recorded a payment on ${pay[1]}${pay[2] ? `, ${lower(pay[2])}` : ''}`;
    const past = pastAction(e.title);
    if (past) return `${who} ${past}`;
  }
  return who ? `${who}: ${e.title}` : e.title;
}

/**
 * The story's way with names: a person's full name the first time the feed (oldest first) names
 * them, their first name after that. Only the heads change; the search text keeps the full names.
 */
function firstNamesAfterFirst(entries: JobEntry[], names: Iterable<string>): void {
  const full = [...new Set([...names].filter((n) => /\s/.test(n)))].sort((a, b) => b.length - a.length);
  if (!full.length) return;
  const seen = new Set<string>();
  const re = new RegExp(full.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'), 'g');
  for (const e of [...entries].sort((a, b) => a.at.localeCompare(b.at))) {
    if (typeof e.head !== 'string') continue;
    e.head = e.head.replace(re, (n) => {
      if (seen.has(n)) return firstName(n);
      seen.add(n);
      return n;
    });
  }
}

/** Everything that happened on the job, newest first in the feed: its own key moments, the timeline,
 *  its notes, the texts with the customer, the thread with the techs and the history it was imported with. */
export function historyEntries(d: JobPageData, timeZone: string): JobEntry[] {
  const at = (iso: string) => localAt(iso, timeZone);
  const out: JobEntry[] = [];
  const { more, job } = d;
  out.push({
    id: 'k-booked',
    at: at(more.booked.at),
    kind: 'book',
    head: more.booked.by ? `${more.booked.by} booked the job` : 'The job was booked',
    text: `booked ${more.booked.by ?? ''}`,
    isKey: true,
  });
  if (more.completed)
    out.push({
      id: 'k-completed',
      at: at(more.completed.at),
      kind: 'edit',
      head: more.completed.by ? `${more.completed.by} completed the job` : 'The job was completed',
      text: `completed ${more.completed.by ?? ''}`,
      isKey: true,
    });
  if (more.cancelled)
    out.push({
      id: 'k-cancelled',
      at: at(more.cancelled.at),
      kind: 'edit',
      head: more.cancelled.by ? `${more.cancelled.by} cancelled the job` : 'The job was cancelled',
      text: `cancelled ${more.cancelled.reason ?? ''}`,
      ...(more.cancelled.reason ? { quote: more.cancelled.reason } : {}),
      isKey: true,
    });
  if (more.sentBack)
    out.push({
      id: 'k-sentback',
      at: at(more.sentBack.at),
      kind: 'edit',
      head: 'Sent back to the tech from review',
      text: `sent back ${more.sentBack.reason ?? ''}`,
      ...(more.sentBack.reason ? { quote: more.sentBack.reason } : {}),
      isKey: true,
    });
  const notes = new Set((job.notes ?? []).map((n) => n.id));
  const texts = new Set((d.messages?.messages ?? []).map((m) => m.id));
  const jobStamps = [more.booked.at, more.completed?.at, more.cancelled?.at, more.sentBack?.at];
  const visitOrder = [...job.appointments].sort((a, b) => a.start.localeCompare(b.start)).map((a) => a.id);
  const visitTechs = new Map(
    job.appointments.map((a) => [
      a.id,
      (more.visits.find((v) => v.appointmentId === a.id)?.techs ?? a.techs ?? []).map((t) => t.name),
    ]),
  );
  const people = new Set<string>(
    [more.booked.by, more.completed?.by, more.cancelled?.by].filter((n): n is string => !!n),
  );
  for (const ts of visitTechs.values()) for (const n of ts) people.add(n);
  const sentenceCtx = {
    visitNo: (id: string) => (visitOrder.indexOf(id) >= 0 ? visitOrder.indexOf(id) + 1 : null),
    techsOf: (id: string) => visitTechs.get(id) ?? [],
  };
  const action = (e: TimelineEvent) => e.title.replace(/^(Booking|Dispatch): /, '');
  const isComplete = (e: TimelineEvent) => /^Complete a job$/.test(action(e));
  const reopened = (d.timeline?.events ?? []).some((e) => e.kind === 'office' && /^Reopen/.test(action(e)));
  for (const [i, e] of (d.timeline?.events ?? []).entries()) {
    if (e.entityType === 'note' && notes.has(e.entityId)) continue; // the note's own row says it
    if (e.entityType === 'message' && texts.has(e.entityId)) continue;
    // the job's own booking, completing, cancelling or sending back is its key moment's row
    if (e.kind === 'office' && e.entityType === 'job' && jobStamps.some((s) => near(e.at, s))) continue;
    // completing the job finalizes its invoice: that action, on the invoice, is the Completed row too
    // (any time stamp when the job was never reopened, so it was completed only once)
    if (
      e.kind === 'office' &&
      isComplete(e) &&
      more.completed &&
      (near(e.at, more.completed.at) || !reopened)
    )
      continue;
    const head = timelineSentence(e, sentenceCtx);
    if (head === null) continue;
    if (e.who) for (const n of e.who.split(' for ')) people.add(n);
    out.push({
      id: `t-${i}-${e.entityId}`,
      at: at(e.at),
      kind: kindOf(e),
      head,
      text: join(head, e.detail),
      ...(e.detail ? { detail: e.detail } : {}),
      ...(keyMoment(e) ? { isKey: true } : {}),
      ...(e.kind === 'photo' && e.entityType === 'attachment' ? { photos: [e.entityId] } : {}),
    });
  }
  for (const n of job.notes ?? [])
    out.push({
      id: `n-${n.id}`,
      at: at(n.at),
      kind: 'note',
      head: `${n.author} wrote a note${n.appointmentId ? ' on a visit' : ''}`,
      text: `${n.author} ${n.body}`,
      quote: plainText(n.body),
    });
  for (const m of d.messages?.messages ?? []) {
    const inbound = m.direction === 'in';
    const how = m.channel === 'sms' ? 'text' : 'email';
    const head = inbound
      ? `${m.customerName ?? m.from ?? 'The customer'} sent a ${how}`
      : `${how === 'text' ? 'Texted' : 'Emailed'} ${m.channel === 'sms' ? phoneText(m.to) : m.to}${m.status === 'failed' ? ' · didn’t send' : ''}`;
    out.push({
      id: `m-${m.id}`,
      at: at(m.receivedAt ?? m.sentAt ?? m.queuedAt),
      kind: 'text',
      head,
      text: join(head, m.subject, m.body),
      quote: m.subject ? `${m.subject}: ${m.body}` : m.body,
    });
  }
  for (const m of d.techThread?.messages ?? []) {
    const who = m.author.name ?? (m.fromOffice ? 'The office' : 'A tech');
    const head = m.fromOffice ? `${who} to the techs` : `${who} to the office`;
    out.push({
      id: `j-${m.id}`,
      at: at(m.sentAt),
      kind: 'text',
      head,
      text: `${head} ${m.body}`,
      quote: m.body,
    });
  }
  for (const [i, e] of (job.importedHistory ?? []).entries()) {
    const cut = e.text.indexOf(': ');
    const event =
      e.kind === 'history'
        ? cut > 0
          ? e.text.slice(0, cut)
          : e.text
        : e.kind === 'hold_reason'
          ? 'On hold'
          : 'Cancelled';
    const detail = e.kind === 'history' ? (cut > 0 ? e.text.slice(cut + 2) : null) : e.text;
    out.push({
      id: `i-${i}`,
      at: at(e.at),
      kind: 'edit',
      head: `Imported: ${event}`,
      text: join('Imported', event, detail),
      ...(detail ? { detail: plainText(detail) } : {}),
    });
  }
  for (const n of job.notes ?? []) people.add(n.author);
  for (const m of d.techThread?.messages ?? []) if (m.author.name) people.add(m.author.name);
  firstNamesAfterFirst(out, people);
  return out;
}

/** The job's story along the top: booked, each visit, sold, done or cancelled; each stop opens its row. */
function stripOf(
  d: JobPageData,
  o: ViewOptions,
  entries: JobEntry[],
  visits: JobVisit[],
  s: JobState,
): JobView['strip'] {
  const tz = o.timeZone;
  type Raw = {
    id: string;
    label: string;
    at: string;
    text: string;
    pens?: number[];
    isNow?: boolean;
    isGone?: boolean;
    waits?: boolean;
  };
  const raw: Raw[] = [{ id: 'k-booked', label: 'Booked', at: d.more.booked.at, text: 'Booked' }];
  const arrived = (visitId: string) =>
    entries.find((e) => e.id.endsWith(`-${visitId}`) && /Arrived/.test(String(e.head)));
  const byId = new Map(d.job.appointments.map((a) => [a.id, a]));
  for (const v of visits) {
    const a = byId.get(v.id);
    if (!a) continue;
    raw.push({
      id: arrived(v.id)?.id ?? `visit-${v.id}`,
      label: `Visit ${v.n}`,
      at: a.start,
      text: `Visit ${v.n}, ${v.window} · ${v.techs} · ${v.word}`,
      pens: v.pens,
      isNow: a.status === 'working' || a.status === 'dispatched',
      isGone: a.status === 'cancelled',
      waits: a.status === 'paused',
    });
  }
  const sold = signedOf(d);
  if (sold?.estimate.signature)
    raw.push({
      id:
        entries.find((e) => e.kind === 'estimate' && /sold|signed/i.test(String(e.head)))?.id ??
        `sold-${sold.estimate.id}`,
      label: 'Sold',
      at: sold.estimate.signature.signedAt,
      text: `${sold.name} signed by ${sold.estimate.signature.signerName}`,
    });
  if (d.more.completed)
    raw.push({ id: 'k-completed', label: 'Done', at: d.more.completed.at, text: 'Completed' });
  if (d.more.cancelled)
    raw.push({
      id: 'k-cancelled',
      label: 'Cancelled',
      at: d.more.cancelled.at,
      text: 'Cancelled',
      isGone: true,
    });
  raw.sort((a, b) => a.at.localeCompare(b.at));
  const day = (iso: string) => dateText(iso, tz, { weekday: 'short', year: false, now: o.now });
  const stops: StoryStop[] = raw.map((x, i) => ({
    id: x.id,
    label: x.label,
    ...(i === 0 || day(raw[i - 1]!.at) !== day(x.at) ? { day: day(x.at) } : {}),
    says: `${x.text} · ${day(x.at)}, ${timeText(x.at, tz)}`,
    ...(x.pens?.length ? { pens: x.pens } : {}),
    ...(x.isNow ? { isNow: true } : {}),
    ...(x.isGone ? { isGone: true } : {}),
    ...(x.waits ? { waitsAfter: true } : {}),
  }));
  return { stops, isWaiting: s === 'onhold' || s === 'review' };
}

function modesOf(d: JobPageData, can: JobCan, mirroring: boolean): ComposeMode[] {
  const modes: ComposeMode[] = [];
  if (can.addNote)
    modes.push({
      id: 'note',
      label: 'Note',
      placeholder: 'Add a note on the job…',
      sendLabel: 'Add the note',
      note: 'The office and the techs on the job see notes.',
    });
  const techs = [...new Set(d.more.visits.flatMap((v) => v.techs.map((t) => first(t.name))))];
  if (can.messageTechs && d.techThread)
    modes.push({
      id: 'techs',
      label: 'Message the techs',
      placeholder: techs.length ? `Message ${techs.join(' and ')}…` : 'Message the techs on the job…',
      sendLabel: techs.length ? `Send to ${techs.join(' and ')}` : 'Send to the techs',
      note: 'It shows on their iPads, on this job.',
    });
  const phone = can.textCustomer && !mirroring ? textablePhone(d.customer) : null;
  if (phone) {
    const who = first(phone.contact || d.job.customerName);
    modes.push({
      id: 'text',
      label: `Text ${who}`,
      placeholder: `Text ${phone.contact || d.job.customerName} at ${phoneText(phone.value)}…`,
      sendLabel: `Text ${who}`,
      note: 'From the office line, on this job.',
    });
  }
  return modes;
}

// ── the side ──

function customerOf(d: JobPageData, can: JobCan, mirroring: boolean): JobView['side']['customer'] {
  const c = d.customer;
  const contacts: JobContact[] = [];
  const textOk = can.textCustomer && !mirroring && !!c && !c.doNotContact;
  for (const k of c?.contacts ?? [])
    for (const m of k.methods) {
      const phone = m.kind === 'phone';
      const actions: JobContact['actions'] = phone
        ? [{ label: 'Call', href: `tel:${m.value}` }]
        : [{ label: 'Email', href: `mailto:${m.value}` }];
      if (phone && textOk && m.consent && !m.doNotContact)
        actions.push({ label: 'Text', action: { id: 'text', label: 'Text', target: m.id } });
      contacts.push({
        id: m.id,
        value: phone ? phoneText(m.value) : m.value,
        note: join(
          k.name !== c?.displayName ? k.name : null,
          k.title,
          phone ? 'Phone' : 'Email',
          m.isPrimary ? 'main' : null,
          m.doNotContact ? 'do not contact' : null,
        ),
        actions,
      });
    }
  const site = d.job.siteContact;
  if (site?.phone)
    contacts.push({
      id: `site-${site.contactId}`,
      value: phoneText(site.phone),
      note: `${site.name} · site contact, hears about the visits`,
      actions: [{ label: 'Call', href: `tel:${site.phone}` }],
    });
  const r = d.more.rating;
  return {
    note: join(
      c ? `Customer #${c.number}` : d.job.customerName,
      c?.kind === 'commercial' ? 'commercial' : null,
      c?.doNotContact ? 'do not contact' : null,
      c?.tags?.length ? c.tags.map((t) => t.name).join(', ') : null,
      r ? `rated this job ${r.stars} of 5` : null,
    ),
    recordLink: { id: 'customer', label: 'Open the record' },
    contacts,
  };
}

function tasksOf(d: JobPageData, o: ViewOptions, can: JobCan): JobView['side']['tasks'] {
  if (!d.tasks) return undefined;
  const tz = o.timeZone;
  const rows = d.tasks.tasks.map((t) => {
    const done = t.status === 'done' || t.status === 'cancelled';
    return {
      id: t.id,
      title: t.title,
      who: t.assignee?.name ?? 'Nobody yet',
      due: t.dueOn
        ? `due ${dateText(t.dueOn, tz, { weekday: 'short', year: false, now: o.now })}${t.overdue ? ', overdue' : ''}`
        : 'no due day',
      ...(done ? { isDone: true } : {}),
      ...(done && t.closedAt ? { doneOn: dateText(t.closedAt, tz, { year: false, now: o.now }) } : {}),
    };
  });
  const next = rows.find((t) => !t.isDone);
  return {
    summary: next ? `${next.title}, ${next.due}` : rows.length ? 'All done' : 'None open',
    rows,
    ...(can.manageTasks ? { add: { id: 'tasks-add', label: 'Add' } } : {}),
    canChange: !!can.manageTasks,
  };
}

function siteOf(d: JobPageData, o: ViewOptions): JobView['side']['site'] {
  const address = fullAddress(d.more.address);
  const a = d.more.access;
  const at: JobView['side']['site']['atAddress'] = [];
  for (const e of d.more.equipmentInstalled) at.push({ label: e.label, value: 'Put in on this job' });
  const loc = d.job.locationId;
  for (const e of d.customer?.equipment ?? []) {
    if (e.locationId !== loc || e.retiredAt || d.more.equipmentInstalled.some((x) => x.id === e.id)) continue;
    at.push({
      label: plainText(e.kind),
      value:
        join(
          [e.make, e.model].filter(Boolean).join(' ') || null,
          e.size,
          e.installedOn ? `put in ${e.installedOn.slice(0, 4)}` : null,
        ) || 'On record',
    });
  }
  if (d.more.membershipVisit)
    at.push({ label: 'Membership', value: `${d.more.membershipVisit.plan}: this job is one of its visits` });
  if (d.more.parentJob)
    at.push({
      label: `#${d.more.parentJob.number}`,
      value: `This is a callback on it: ${plainText(d.more.parentJob.summary)}`,
      link: { id: 'job', label: `#${d.more.parentJob.number}`, target: d.more.parentJob.id },
    });
  for (const cb of d.more.callbacks)
    at.push({
      label: `#${cb.number}`,
      value: `A callback on this job: ${plainText(cb.summary)}`,
      link: { id: 'job', label: `#${cb.number}`, target: cb.id },
    });
  const getIn = join(a?.notes ?? null, a?.codes ? `Codes: ${a.codes}` : null);
  void o;
  return {
    address,
    directionsHref: directionsHref(address),
    ...(getIn ? { gettingIn: getIn } : {}),
    atAddress: at,
  };
}

/**
 * The Job summary area (the job summary, step 1): the fields the owner's own records showed on a job,
 * each only when it has a value: the scope of work (the summary's own words, and a sold job's "Other"),
 * the dispatch notes, who sold it, the work order, the scheduling note, the customer's PO, the buzzer and
 * the directions. The original, whole, when it says more. None from a server before it.
 */
function jobSummaryOf(d: JobPageData): JobView['summary'] {
  const { job, more } = d;
  const p = more.summary;
  if (!p) return undefined;
  const items: JobView['side']['details']['items'] = [];
  const add = (label: string, value: string | null | undefined) => {
    const t = value?.trim();
    if (t) items.push({ label, value: t });
  };
  add('Scope of work', [p.scope, p.other].filter(Boolean).join('\n'));
  add('Dispatch notes', p.dispatchNotes.join('\n'));
  add('Sold by', signedOf(d)?.estimate.soldBy ?? job.soldBy ?? p.soldBy);
  add('Work order on file', p.workOrderOnFile);
  add('Scheduling', p.scheduling);
  const inv = job.invoice && job.invoice.status !== 'void' ? job.invoice : null;
  add('Customer PO', more.imported?.customerPo ?? inv?.poNumber ?? null);
  add('Buzzer', more.imported?.buzzer ?? more.access?.codes ?? null);
  add('Customer directions', more.access?.notes ?? null);
  if (!items.length && !p.changed) return undefined;
  const lead = scopeLead(p.scope, 1);
  const first = items[0];
  return {
    line: lead || (first ? `${first.label}: ${first.value.split('\n')[0]}` : 'Only its job type’s own words'),
    items,
    ...(p.changed && p.original.trim() ? { original: p.original } : {}),
  };
}

const INVOICE_MODES: Record<string, string> = {
  per_job: 'One invoice for the job',
  per_visit: 'An invoice per visit',
};

function detailsOf(d: JobPageData, o: ViewOptions, can: JobCan): JobView['side']['details'] {
  const { job, more } = d;
  const tz = o.timeZone;
  const when = (iso: string) => dateText(iso, tz, { weekday: 'short', year: false, now: o.now });
  const items: JobDetailItem[] = [];
  const add = (label: string, value: string | null | undefined, link?: ActionLink) => {
    if (value) items.push({ label, value, ...(link ? { link } : {}) });
  };
  add('Job type', more.jobType?.name);
  add('Department', job.businessUnitName);
  const facts = jobFactsText({ businessUnitName: null, fromServiceTitan: job.fromServiceTitan ?? null });
  add('Priority and project', facts);
  add('Priority level', more.imported?.priorityLevel);
  add('Tags', more.tags.map((t) => t.name).join(', '));
  if (more.isWarranty) add('Warranty', 'A warranty callback');
  const click = d.adClick?.click;
  add(
    'Came from',
    join(
      job.campaign ? campaignWord(job.campaign) : null,
      click ? clickLine(click) : null,
      more.sourceCall
        ? `the call ${when(more.sourceCall.at)}${more.sourceCall.from ? ` from ${phoneText(more.sourceCall.from)}` : ''}`
        : null,
      more.sourceLead ? `a lead${more.sourceLead.name ? `, ${more.sourceLead.name}` : ''}` : null,
    ),
  );
  add('Booked', `${when(more.booked.at)}${more.booked.by ? ` by ${more.booked.by}` : ''}`);
  const s = signedOf(d);
  add('Sold by', s?.estimate.soldBy ?? job.soldBy ?? null);
  if (more.soldFromEstimate)
    add(
      'Sold on',
      `Estimate ${more.soldFromEstimate.number}, job #${more.soldFromEstimate.jobNumber}`,
      more.soldFromEstimate.jobId !== job.id
        ? {
            id: 'job',
            label: `Estimate ${more.soldFromEstimate.number}, job #${more.soldFromEstimate.jobNumber}`,
            target: more.soldFromEstimate.jobId,
          }
        : undefined,
    );
  if (more.completed)
    add('Completed', `${when(more.completed.at)}${more.completed.by ? ` by ${more.completed.by}` : ''}`);
  if (more.cancelled)
    add(
      'Cancelled',
      join(
        `${when(more.cancelled.at)}${more.cancelled.by ? ` by ${more.cancelled.by}` : ''}`,
        more.cancelled.reason,
      ),
    );
  // The customer's PO, on the invoice; a customer who needs one on every invoice says so before there
  // is an invoice to put it on (QA-743)
  const inv = job.invoice && job.invoice.status !== 'void' ? job.invoice : null;
  const po = inv?.poNumber ?? null;
  const mayPo = !!inv && !!can.setPo && !o.mirroring && inv.status !== 'paid';
  if (po) add('Customer PO', po, mayPo ? { id: 'po', label: po } : undefined);
  else if (job.poRequired)
    add(
      'Customer PO',
      'Required, none yet',
      mayPo ? { id: 'po', label: 'Required, none yet: add it' } : undefined,
    );
  else if (mayPo) add('Customer PO', 'Add one', { id: 'po', label: 'Add one' });
  add(
    'Invoicing',
    more.invoiceMode ? (INVOICE_MODES[more.invoiceMode] ?? more.invoiceMode.replace(/_/g, ' ')) : null,
  );
  if (more.rating) add('Rating', join(`${more.rating.stars} of 5`, more.rating.comment));
  if (job.timeLogged?.length)
    add('Time', job.timeLogged.map((t) => `${t.name}: ${timeLoggedText(t)}`).join('; '));
  return {
    summary:
      join(more.jobType?.name, job.businessUnitName, more.isWarranty ? 'warranty' : null) ||
      'The job’s facts',
    items,
  };
}

// ── the top ──

function standingOf(d: JobPageData, o: ViewOptions, s: JobState, visits: JobVisit[]): JobView['standing'] {
  const tz = o.timeZone;
  const { job, more } = d;
  const live = visits.filter((v) => v.drawn !== 'cancelled');
  const next = live.find((v) => v.drawn === 'booked');
  const inv = job.invoice;
  const word = s === 'booked' && !next ? TO_SCHEDULE : JOB_STATE_WORDS[s];
  const base = { mark: JOB_STATE_MARK[s], word };
  switch (s) {
    case 'booked':
      return {
        ...base,
        line: next
          ? `Visit ${next.n}, ${next.day}, ${next.window} with ${next.techs}`
          : 'No visit booked yet',
      };
    case 'onhold': {
      const p = [...visits].reverse().find((v) => v.drawn === 'paused');
      const why = p ? more.visits.find((v) => v.appointmentId === p.id)?.pauseReason : null;
      return { ...base, line: p ? `Visit ${p.n} stopped${why ? `: ${why}` : ''}` : 'Waiting' };
    }
    case 'underway': {
      const w = live.find((v) => v.drawn === 'working');
      return { ...base, line: w ? `Visit ${w.n}, ${w.techs}, ${w.word}` : 'Under way' };
    }
    case 'review':
      return { ...base, line: 'The techs are done; the office checks it before it’s completed' };
    case 'unpaid':
      return {
        ...base,
        line: inv ? `Invoice ${inv.number}, ${invoiceState(inv).toLowerCase()}` : 'Completed',
        ...(inv && inv.balanceCents > 0 ? { end: ' owing', endStrong: money(inv.balanceCents) } : {}),
      };
    case 'paid':
      return {
        ...base,
        line: inv
          ? `Invoice ${inv.number} paid, ${money(inv.totalCents)}`
          : more.completed
            ? `Completed ${dateText(more.completed.at, tz, { year: false, now: o.now })}`
            : 'Completed',
      };
    case 'cancelled':
      return {
        ...base,
        line: join(
          more.cancelled
            ? `Cancelled ${dateText(more.cancelled.at, tz, { weekday: 'short', year: false, now: o.now })}`
            : 'Cancelled',
          more.cancelled?.reason ?? null,
        ),
      };
  }
}

/** "See it on the board" beside a "Day board" crumb says it twice: on a phone, only the crumb. */
function notTwice(p: JobView['primary'], crumb: string): JobView['primary'] {
  return p?.id === 'board' && crumb === pageLabel('board') ? { ...p, hideOnPhone: true } : p;
}

function primaryOf(
  d: JobPageData,
  s: JobState,
  can: JobCan,
  mirroring: boolean,
  visits: JobVisit[],
): JobView['primary'] {
  const live = cancellable(d.job.status);
  if (s === 'review' && can.complete && !mirroring) return { id: 'complete', label: 'Complete the job' };
  if (
    (s === 'booked' || s === 'onhold') &&
    can.addVisit &&
    !mirroring &&
    !visits.some((v) => v.drawn === 'booked')
  )
    return { id: 'addvisit', label: 'Add a visit' };
  if (live && visits.some((v) => v.drawn !== 'cancelled' && v.drawn !== 'done'))
    return { id: 'board', label: 'See it on the board' };
  if (s === 'unpaid' && d.job.invoice) return { id: 'invoice', label: 'Open the invoice' };
  return null;
}

function menuOf(
  d: JobPageData,
  s: JobState,
  can: JobCan,
  o: ViewOptions,
  review: JobReviewView | undefined,
): MenuSection[] {
  const { job } = d;
  const live = cancellable(job.status);
  const m = !!o.mirroring;
  const reopenFirst = s === 'cancelled' ? 'A cancelled job stays cancelled' : 'Reopen the job first';
  const work: MenuItem[] = [];
  if (can.addVisit && !m)
    work.push({
      id: 'addvisit',
      label: 'Add a visit',
      ...(live ? {} : { isDisabled: true, reason: reopenFirst }),
    });
  if (can.buildEstimates && !m)
    work.push({
      id: 'estimate-new',
      label: 'New estimate',
      ...(live ? {} : { isDisabled: true, reason: reopenFirst }),
    });
  if (can.manageTasks && d.tasks) work.push({ id: 'tasks-add', label: 'Add a task' });
  if (d.messages) work.push({ id: 'messages', label: 'Every text and email' });
  const job2: MenuItem[] = [];
  if (review?.sendBackTo) job2.push({ id: 'sendback', label: `Send back to ${review.sendBackTo}…` });
  if (can.setSplits && !m && d.review && d.review.splits.length > 1)
    job2.push({ id: 'splits', label: 'Who shares the credit…' });
  if (can.reopen && !m && job.status === 'completed')
    job2.push({ id: 'reopen', label: 'Reopen for a warranty callback…' });
  if (can.voidInvoice && !m && job.invoice && job.invoice.status !== 'void' && job.invoice.status !== 'draft')
    job2.push({ id: 'void', label: `Void invoice ${job.invoice.number}…` });
  job2.push({ id: 'copy', label: 'Copy the link' });
  const sections: MenuSection[] = [];
  if (work.length) sections.push({ items: work });
  sections.push({ items: job2 });
  if (can.cancel && !m && job.status !== 'cancelled')
    sections.push({
      items: [
        {
          id: 'cancel',
          label: 'Cancel the job…',
          ...(live
            ? {}
            : {
                isDisabled: true,
                reason: 'A completed job can’t be cancelled. Reopen it, or void the invoice.',
              }),
        },
      ],
    });
  return sections;
}

/**
 * What Complete's dialog says. It never says the customer is asked to rate the job when the company
 * can't text yet (QA-324, QA-744): the rating request is a text.
 */
function completeBody(inv: JobDetail['invoice'], lead: string | null, textingOff: boolean): string {
  return (
    join(
      inv ? `Invoice ${inv.number} is finalized for ${money(inv.totalCents)}` : 'The job moves to Done',
      textingOff
        ? 'the shares and forms are locked and any paused visits close'
        : 'the shares and forms are locked, any paused visits close and the customer is asked to rate the job',
      lead ? `${first(lead)}’s visits are closed for it` : null,
    ).replace(/ · /g, '; ') +
    (textingOff
      ? '. The customer isn’t asked to rate the job: texting isn’t set up for your company yet.'
      : '.') +
    ' Send the invoice from it afterwards.'
  );
}

/** The page's view of a job, from jobs.page's answer, for this viewer. */
/**
 * The system roles' keys (packages/auth roles.ts SYSTEM_ROLES): a "name" that is only one of them is
 * a sign-in with no person's name on it, never shown as it is.
 */
const ROLE_KEYS = new Set(['owner', 'manager', 'dispatcher', 'csr', 'technician', 'bookkeeper', 'warehouse']);

/** A person's name as the page may say it: null for none, or for a role's key ("dispatcher"). */
export const personName = (name: string | null | undefined): string | null =>
  name && !ROLE_KEYS.has(name.trim().toLowerCase()) ? name : null;

/**
 * The read with every person named as the page may say them (round 3, the lead 26 Oct: "never a role
 * key"): a file's uploader and a History row's who; a role's key reads as the office.
 */
export function namedPeople(d: JobPageData): JobPageData {
  const who = (w: string) =>
    w
      .split(' for ')
      .map((n) => personName(n) ?? 'The office')
      .join(' for ');
  return {
    ...d,
    ...(d.photos
      ? {
          photos: {
            ...d.photos,
            attachments: d.photos.attachments.map((a) => ({ ...a, addedBy: personName(a.addedBy) })),
          },
        }
      : {}),
    ...(d.timeline
      ? {
          timeline: {
            ...d.timeline,
            events: d.timeline.events.map((e) => (e.who ? { ...e, who: who(e.who) } : e)),
          },
        }
      : {}),
  };
}

export function toJobView(page: JobPageData, o: ViewOptions): JobView {
  const d = namedPeople(page);
  const { job } = d;
  const can = d.can;
  const mirroring = !!o.mirroring;
  const s = jobStateOf({
    status: job.status,
    visits: job.appointments,
    balanceCents: job.invoice?.balanceCents ?? null,
  });
  const rows = visitRows(d, o, can);
  const live = cancellable(job.status);
  const review = reviewOf(d, o, can, s);
  const entries = historyEntries(d, o.timeZone);
  const logged = job.timeLogged ?? [];
  const working = logged.reduce((a, t) => a + t.workMinutes, 0);
  const lead = leadTech(d);
  const inv = job.invoice;
  return {
    id: job.id,
    number: job.number,
    state: s,
    // the work's first line; a server before the summary's parts: the summary as text
    title:
      (d.more.summary
        ? scopeLead(d.more.summary.scope, 1) || d.more.jobType?.name
        : plainText(job.summary)) || `Job ${job.number}`,
    crumb: { id: 'crumb', label: o.crumb },
    customer: { name: job.customerName, link: { id: 'customer', label: job.customerName } },
    address: job.address,
    standing: standingOf(d, o, s, rows),
    primary: notTwice(primaryOf(d, s, can, mirroring, rows), o.crumb),
    menu: menuOf(d, s, can, o, review),
    opener: { reviewer: !!can.complete, fromVisit: !!o.fromVisit },
    strip: stripOf(d, o, entries, rows, s),
    visits: {
      summary: rows.length
        ? join(
            plural(rows.filter((v) => v.drawn !== 'cancelled').length, 'visit'),
            (() => {
              const next = rows.find((v) => v.drawn === 'booked');
              return next ? `next ${next.day}, ${next.window}` : null;
            })(),
          )
        : 'None booked yet',
      rows,
      ...(working ? { onSite: logged.map((t) => `${first(t.name)} ${timeLoggedText(t)}`).join(' · ') } : {}),
      actions: can.addVisit && !mirroring && live ? [{ id: 'addvisit', label: 'Add a visit' }] : [],
    },
    ...(jobSummaryOf(d) ? { summary: jobSummaryOf(d)! } : {}),
    ...(review ? { review } : {}),
    ...(estimatesOf(d, o, can) ? { estimates: estimatesOf(d, o, can)! } : {}),
    ...(partsOf(d, can) ? { parts: partsOf(d, can)! } : {}),
    ...(invoiceOf(d, o, can) ? { invoice: invoiceOf(d, o, can)! } : {}),
    ...(photosOf(d, o, can, rows) ? { photos: photosOf(d, o, can, rows)! } : {}),
    history: {
      entries,
      filters: HISTORY_FILTERS,
      today: localDateOf(o.now, o.timeZone),
      modes: modesOf(d, can, mirroring),
    },
    side: {
      customer: customerOf(d, can, mirroring),
      ...(tasksOf(d, o, can) ? { tasks: tasksOf(d, o, can)! } : {}),
      site: siteOf(d, o),
      details: detailsOf(d, o, can),
    },
    dialogs: {
      ...(s === 'review' && can.complete && !mirroring
        ? {
            complete: {
              title: `Complete job ${job.number}?`,
              body: completeBody(inv, lead, !!o.textingOff),
            },
          }
        : {}),
    },
    ...(o.notice ? { notice: o.notice } : {}),
  };
}
